/**
 * Health Container Module — Whoop-powered health dashboard.
 * Sparkline chart + metric cards with color-coded sources.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import {
  listModuleSidebarPreferences,
  updateModuleSidebarPreference,
} from '@/lib/api/endpoints/workspaces';
import MetricsSettingsPanel from './MetricsSettingsPanel';
import { useToast } from '@/app/providers/ToastContext';
import { openChatStream } from '@/lib/api/endpoints/chat';
import type { Module } from '@/types/modules';
import HealthProfile from './HealthProfile';
import DailyLogView from './DailyLogView';
import AddMealModal from './AddMealModal';
import LabResultsView from './LabResultsView';
import { ModuleSearchBar } from '@/common/components/ModuleSearchBar';
import { MobileSearchToggle } from '@/common/components/MobileSearchToggle';
import { MobileActionButton } from '@/common/components/MobileActionButton';
import { useIsCompactLayout } from '@/common/hooks';
import { ModuleEmptyState } from '../_infrastructure/ModuleEmptyState';
import {
  PROVIDER_COLORS,
  SOURCE_LABELS,
  PROVIDER_DEFAULT_STARS,
  WEARABLE_OPTIONS,
} from './constants';
import { metricOf, providerOf } from './utils/metric-keys';
import { getSourceColor } from './utils/source-color';
import { Sparkline } from './components/Sparkline';
import { parseLabResults } from './utils/lab-report-parser';
import { buildInsights } from './utils/insights';
import { useHealthData, HEALTH_STATS_QUERY_KEY } from './hooks/useHealthData';
import { useWearable } from './hooks/useWearable';
import { SwitchWearableModal } from './components/SwitchWearableModal';
import { ObjectivesModal } from './components/ObjectivesModal';
import { readSessionPersisted, writeSessionPersisted } from '@/common/hooks/useSessionPersistedState';
import {
  readHealthActiveTab,
  writeHealthActiveTab,
  readHealthConsentDismissed,
  writeHealthConsentDismissed,
  readHealthWearableSkipped,
  writeHealthWearableSkipped,
} from './healthLocalStoragePersistence';


/** Check if a key matches any entry in a preferences list, handling legacy unprefixed keys.
 *  - "oura/restingHeartRate" matches pref "oura/restingHeartRate" (exact)
 *  - "oura/restingHeartRate" matches legacy pref "restingHeartRate" ONLY if no other provider's
 *    version of that metric is explicitly in prefs (prevents one legacy pref matching all providers)
 *  - Unprefixed keys match as-is */
function prefsMatch(prefs: string[], key: string): boolean {
  if (prefs.includes(key)) return true;
  const m = metricOf(key);
  const prov = providerOf(key);
  // Only fall back to unprefixed match if the key has no provider, OR
  // the prefs list has the unprefixed version but no provider-prefixed versions of this metric
  if (!prov) return prefs.includes(m);
  if (prefs.includes(m) && !prefs.some(p => p.includes('/') && metricOf(p) === m)) return true;
  return false;
}




interface HealthContainerProps {
  module: Module;
  conversationId: string;
}


export default function HealthContainerModule({ module, conversationId }: HealthContainerProps) {
  const { token, selectedContainer } = useSession();
  const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const isMobile = useIsCompactLayout();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTabState] = useState<'log' | 'trends' | 'profile' | 'labs'>(() => readHealthActiveTab(module.id));
  const setActiveTab = (tab: 'log' | 'trends' | 'profile' | 'labs') => {
    setActiveTabState(tab);
    writeHealthActiveTab(module.id, tab);
  };

  const {
    activeWearable,
    connecting,
    syncing,
    handleConnectProvider,
    handleDisconnectWearable,
    handleSyncWearable,
    setPendingMenuSync,
  } = useWearable({ token, conversationId, moduleId: module.id });

  const { allItems, grouped, chartData, summaryCards, refetch } = useHealthData({
    token,
    workspaceId,
    searchQuery,
    providerHint: activeWearable?.provider,
  });

  const handleSilentSendMessage = useCallback(async (message: string, images?: Array<{ mediaType: string; data: string; filename: string }>) => {
    const isLabs = activeTab === 'labs';
    const phrases = ['Working on it...', 'Crunching the numbers...', 'On it...', 'Logging that...', 'One moment...', 'Almost there...'];
    showToast(isLabs ? 'Reading lab report...' : phrases[Math.floor(Math.random() * phrases.length)], 'info');
    try {
      // Call the agent directly via SSE — bypass ChatContext to avoid provider dependency
      // skipHistory prevents the LLM from seeing prior food-logging messages and re-logging them
      for await (const event of openChatStream(token ?? null, {
        messages: [{ role: 'user', content: message }],
        conversationId,
        workspaceId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        imageAttachments: images,
        activeModuleId: module.id,
        activeModuleObjectType: 'health',
        activeModuleLabel: 'Health',
        skipHistory: true,
      })) {
        if (event.type === 'error' || (event.data as any)?.type === 'error') {
          throw new Error((event.data as any).error || (event.data as any)?.message || 'Agent error');
        }
      }
      showToast(isLabs ? 'Lab report added' : 'Entry logged', 'success');
      queryClient.invalidateQueries({ queryKey: ['health-profile'] });
      refetch();
      // Backup refetch after short delay for DB write lag
      setTimeout(() => refetch(), 1500);
    } catch (err) {
      showToast(isLabs ? 'Failed to process lab report' : 'Failed to log entry', 'error');
      throw err;
    }
  }, [token, conversationId, workspaceId, showToast, queryClient, activeTab, refetch]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);

  const handleAddEntry = useCallback(async (message: string, photo?: File) => {
    if (!message.trim() && !photo) return;
    setAddSubmitting(true);
    const isLabs = activeTab === 'labs';
    try {
      let images: Array<{ mediaType: string; data: string; filename: string }> | undefined;
      if (photo) {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.readAsDataURL(photo);
        });
        images = [{ mediaType: photo.type || 'image/jpeg', data: base64, filename: photo.name }];
      }
      if (isLabs && !photo) {
        // Text-based lab results: create the stat directly via API.
        const today = new Date().toISOString().slice(0, 10);
        const dateMatch = message.match(/(\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+\d{1,2},?\s+\d{4})/i);
        const reportDate = dateMatch ? new Date(dateMatch[1]).toISOString().slice(0, 10) : today;

        const labResults = parseLabResults(message);

        await api.objects.createObject(token ?? null, {
          type: 'stat',
          label: `Lab Results — ${reportDate}`,
          value: labResults.length,
          unit: 'tests',
          date: `${reportDate}T00:00:00.000Z`,
          source: 'health:labs',
          category: 'labs',
          labResults,
          panelName: 'Lab Report',
          moduleLinks: [{ moduleId: module.id }],
        } as any);
        queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY });
        showToast('Lab report added', 'success');
      } else {
        let prompt: string;
        if (isLabs && photo) {
          prompt = 'Read this lab report image and log all the test results with values, units, and reference ranges using log_labs. Use the date shown on the report, not today.';
        } else if (activeTab === 'profile') {
          prompt = `Update my health profile: ${message.trim()}`;
        } else {
          prompt = photo && !message.trim() ? 'Identify and log the food in this photo' : `Log this food: ${message.trim()}`;
        }
        await handleSilentSendMessage(prompt, images);
      }
      setShowAddModal(false);
    } catch (err) {
      console.error('[HealthAddEntry] Error:', err);
      showToast('Failed to save', 'error');
    } finally {
      setAddSubmitting(false);
    }
  }, [handleSilentSendMessage, activeTab, token, module.id, queryClient, showToast]);

  const [showObjectives, setShowObjectives] = useState(false);
  const [objectiveCalories, setObjectiveCalories] = useState('');
  const [objectiveProtein, setObjectiveProtein] = useState('');

  // Listen for menu events from ModuleRenderer.
  // (setPendingMenuSync comes from useWearable; its effect consumes the flag)
  useEffect(() => {
    const handleSetObjectives = () => setShowObjectives(true);
    const handleMenuSync = () => setPendingMenuSync(true);
    const handleManageWearables = () => setShowSwitchModal(true);
    window.addEventListener('xavior:health-set-objectives', handleSetObjectives);
    window.addEventListener('xavior:health-sync-wearable', handleMenuSync);
    window.addEventListener('xavior:health-manage-wearables', handleManageWearables);
    return () => {
      window.removeEventListener('xavior:health-set-objectives', handleSetObjectives);
      window.removeEventListener('xavior:health-sync-wearable', handleMenuSync);
      window.removeEventListener('xavior:health-manage-wearables', handleManageWearables);
    };
  }, []);

  const handleSaveObjectives = useCallback(async () => {
    const cal = parseInt(objectiveCalories) || 0;
    const prot = parseInt(objectiveProtein) || 0;
    if (cal > 0 || prot > 0) {
      const fields: string[] = [];
      if (cal > 0) fields.push(`daily calorie target: ${cal}`);
      if (prot > 0) fields.push(`daily protein target: ${prot}g`);
      await handleSilentSendMessage(`Update my health profile: ${fields.join(', ')}`);
    }
    setShowObjectives(false);
  }, [objectiveCalories, objectiveProtein, handleSilentSendMessage]);

  const handleAIObjectives = useCallback(async () => {
    await handleSilentSendMessage('Based on my health profile (age, weight, height, goals), recommend daily calorie and protein targets and update my profile with them.');
    setShowObjectives(false);
  }, [handleSilentSendMessage]);

  const [showSwitchModal, setShowSwitchModal] = useState(false);

  const renderWearableButtons = (onSkip?: () => void) => (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, width: '100%', maxWidth: 300, margin: '8px auto 0' }}>
        {WEARABLE_OPTIONS.map((w) => {
          const isConnecting = connecting === w.provider;
          return (
            <button
              key={w.provider}
              type="button"
              disabled={!!connecting}
              onClick={() => handleConnectProvider(w.provider, w.name)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                borderRadius: 8, border: '1px solid var(--color-border, #DADCE0)',
                background: 'transparent', cursor: connecting ? 'wait' : 'pointer',
                fontFamily: "'DM Sans', sans-serif", transition: 'all 0.15s',
                opacity: (!!connecting && !isConnecting) ? 0.5 : 1,
              }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: 7, background: w.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {w.logo}
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{w.name}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{w.desc}</div>
              </div>
              {isConnecting && (
                <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Connecting...</span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <button
          type="button"
          className="module-empty-state__secondary"
          onClick={onSkip ?? (() => {})}
        >
          Use health tracking without a wearable...
        </button>
      </div>
    </>
  );

  const [showMetricsSettings, setShowMetricsSettings] = useState(false);
  const gearRef = useRef<HTMLButtonElement>(null);

  const { data: metricsPrefs } = useQuery({
    queryKey: ['module-preferences', workspaceId, module.id, 'metrics-settings'],
    queryFn: async () => {
      if (!token || !workspaceId) return null;
      const prefs = await listModuleSidebarPreferences(token, workspaceId);
      const modulePref = prefs.find((p: any) => p.moduleId === module.id);
      return modulePref?.settings ?? null;
    },
    enabled: Boolean(token && workspaceId),
    staleTime: 60_000,
  });

  // Optimistic local state — initialized from server, updated immediately on toggle.
  // Seeded synchronously from the last-known-good server prefs cached in
  // sessionStorage (#5579, third finding) so a cold module remount (chat-pane
  // switch-away/back) paints the user's REAL starred/hidden set on the FIRST
  // frame instead of a hardcoded provider-default set that then reflows once
  // `metricsPrefs` resolves. Falls through to `null` (-> provider defaults)
  // only on the very first-ever load for this module, before anything has
  // been cached.
  const providerDefaults = activeWearable ? PROVIDER_DEFAULT_STARS[activeWearable.provider] ?? [] : [];
  const metricsStarredCacheKey = `xavior:health.metrics-starred:${module.id}`;
  const metricsHiddenCacheKey = `xavior:health.metrics-hidden:${module.id}`;
  const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');
  const [localStarred, setLocalStarred] = useState<string[] | null>(() =>
    readSessionPersisted<string[] | null>(metricsStarredCacheKey, null, (v): v is string[] | null => v === null || isStringArray(v)),
  );
  const [localHidden, setLocalHidden] = useState<string[] | null>(() =>
    readSessionPersisted<string[] | null>(metricsHiddenCacheKey, null, (v): v is string[] | null => v === null || isStringArray(v)),
  );

  // Sync from server exactly once it resolves. Ref-gated rather than
  // `localStarred === null` — a session-cached seed value is provisional, so
  // the ACTUAL server response must still land once per mount and can correct
  // a stale/cross-device cache; gating on nullness would let a truthy cached
  // value permanently suppress that correction.
  const syncedMetricsFromServerRef = useRef(false);
  useEffect(() => {
    if (metricsPrefs && !syncedMetricsFromServerRef.current) {
      syncedMetricsFromServerRef.current = true;
      setLocalStarred(metricsPrefs.starredMetrics ?? null);
      setLocalHidden(metricsPrefs.hiddenMetrics ?? null);
    }
  }, [metricsPrefs]);

  // Write-back so the NEXT cold mount has a fresh cache to seed from (covers
  // both the server-sync above and the optimistic toggle in handleMetricsUpdate).
  useEffect(() => {
    writeSessionPersisted(metricsStarredCacheKey, localStarred);
  }, [metricsStarredCacheKey, localStarred]);
  useEffect(() => {
    writeSessionPersisted(metricsHiddenCacheKey, localHidden);
  }, [metricsHiddenCacheKey, localHidden]);

  const starredMetrics: string[] = localStarred ?? metricsPrefs?.starredMetrics ?? providerDefaults;
  const hiddenMetrics: string[] = localHidden ?? metricsPrefs?.hiddenMetrics ?? [];

  const handleMetricsUpdate = useCallback(async (newStarred: string[], newHidden: string[]) => {
    // An explicit user edit always wins over a still-pending INITIAL prefs
    // fetch — mark the sync as already "done" so the first `metricsPrefs`
    // resolution (which can be a stale response predating this edit, if the
    // user toggled before the very first fetch settled) doesn't clobber it.
    syncedMetricsFromServerRef.current = true;
    // Update local state immediately for instant UI feedback; persist in background.
    setLocalStarred(newStarred);
    setLocalHidden(newHidden);
    if (!token || !workspaceId) return;
    try {
      await updateModuleSidebarPreference(token, workspaceId, module.id, {
        settings: { starredMetrics: newStarred, hiddenMetrics: newHidden },
      });
      queryClient.invalidateQueries({ queryKey: ['module-preferences', workspaceId, module.id, 'metrics-settings'] });
    } catch (err) {
      console.error('Failed to save metric preferences:', err);
    }
  }, [token, workspaceId, module.id, queryClient]);

  const hasData = Object.keys(grouped).length > 0 || Object.keys(chartData).length > 0;

  const [consentDismissed, setConsentDismissed] = useState(() => readHealthConsentDismissed(module.id));
  const dismissConsent = () => {
    setConsentDismissed(true);
    writeHealthConsentDismissed(module.id);
  };

  const [wearableSkipped, setWearableSkipped] = useState(() => readHealthWearableSkipped(module.id));
  const skipWearable = useCallback(() => {
    setWearableSkipped(true);
    writeHealthWearableSkipped(module.id);
  }, [module.id]);

  return (
    <div className="module module-health" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Tab bar — always visible so users can navigate even from welcome */}
      <div className="module-nav">
        <div className="module-nav__primary">
          <div className="module-toolbar-button-group">
            <button className={`module-toolbar-button health-tab ${activeTab === 'log' ? 'is-active' : ''}`} onClick={() => setActiveTab('log')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span className="module-toolbar-label" data-text="Log">Log</span>
            </button>
            <button className={`module-toolbar-button health-tab ${activeTab === 'trends' ? 'is-active' : ''}`} onClick={() => setActiveTab('trends')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
              <span className="module-toolbar-label" data-text="Trends">Trends</span>
            </button>
            <button className={`module-toolbar-button health-tab ${activeTab === 'labs' ? 'is-active' : ''}`} onClick={() => setActiveTab('labs')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6v11l4.72 8.26a1 1 0 0 1-.87 1.49H5.15a1 1 0 0 1-.87-1.49L9 14z"/></svg>
              <span className="module-toolbar-label" data-text="Labs">Labs</span>
            </button>
            <button className={`module-toolbar-button health-tab ${activeTab === 'profile' ? 'is-active' : ''}`} onClick={() => setActiveTab('profile')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              <span className="module-toolbar-label" data-text="Profile">Profile</span>
            </button>
          </div>
        </div>
        <div className="module-nav__secondary" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <MobileSearchToggle targetId="health-search-toolbar" />
          <MobileActionButton onClick={() => setShowAddModal(true)} title={activeTab === 'labs' ? 'Add labs' : activeTab === 'profile' ? 'Add to profile' : 'Log'} />
          {!isMobile && activeWearable && (
            <button
              type="button"
              className="module-toolbar-button"
              onClick={handleSyncWearable}
              disabled={syncing}
              title="Sync wearable data"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: syncing ? 'health-spin 1s linear infinite' : undefined }}>
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              <span className="module-toolbar-label">{syncing ? 'Syncing' : 'Sync'}</span>
            </button>
          )}
          {!isMobile && !activeWearable && (
            <button
              type="button"
              className="module-toolbar-button"
              onClick={() => setShowSwitchModal(true)}
              title="Connect wearable"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="7" ry="7" />
                <line x1="12" y1="18" x2="12" y2="18.01" />
              </svg>
              <span className="module-toolbar-label">Connect</span>
            </button>
          )}
        </div>
      </div>

      <div id="health-search-toolbar" className="module-toolbar module-search-row">
        <ModuleSearchBar standalone={false} value={searchQuery} onChange={setSearchQuery} onClose={() => setSearchQuery('')} placeholder="Search health data..." resultCount={searchQuery ? allItems.length : undefined} autoFocus={false} />
        {!isMobile && activeTab === 'log' && (
          <button type="button" className="module-toolbar-button is-primary" onClick={() => setShowAddModal(true)}>+ Log</button>
        )}
        {!isMobile && activeTab === 'labs' && (
          <button type="button" className="module-toolbar-button is-primary" onClick={() => setShowAddModal(true)}>+ Labs</button>
        )}
        {!isMobile && activeTab === 'profile' && (
          <button type="button" className="module-toolbar-button is-primary" onClick={() => setShowAddModal(true)}>+ Profile</button>
        )}
      </div>

      {!consentDismissed && (
        <div style={{
          margin: '0 16px', padding: '10px 12px', borderRadius: 8,
          background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.12)',
          display: 'flex', alignItems: 'flex-start', gap: 8,
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>
            Your health data is stored privately in your workspace and is not shared with anyone. You control what you add and can delete it at any time.
          </div>
          <button
            onClick={dismissConsent}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0,
              color: 'var(--color-text-tertiary)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px' }}>
        {activeTab === 'log' && (!activeWearable && allItems.length === 0 && !wearableSkipped ? (
          <>
            <ModuleEmptyState
              icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
              iconColor="#ec4899"
              title="AI-powered health tracking"
              description={<>Connect a wearable to sync your health data automatically.<br />Track sleep, recovery, activity, and more with AI insights.</>}
            />
            {renderWearableButtons(skipWearable)}
          </>
        ) : (
          <DailyLogView moduleId={module.id} healthStats={allItems} onSyncWhoop={handleSyncWearable} onReconnectWhoop={() => setShowSwitchModal(true)} syncing={syncing} connecting={!!connecting} whoopConnected={!!activeWearable} onStatsChanged={() => queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY })} onSilentSendMessage={handleSilentSendMessage} onOpenLogModal={() => setShowAddModal(true)} onSaveVitals={async (data) => {
            try {
              const today = new Date().toISOString().slice(0, 10);
              if (data.weight) {
                await api.objects.createObject(token ?? null, {
                  type: 'stat', label: 'Weight', value: data.weight, unit: 'lbs',
                  date: `${today}T00:00:00.000Z`, source: 'health:vitals', category: 'health',
                  metricKey: `health:vitals:weight:${today}`,
                  moduleLinks: [{ moduleId: module.id }],
                } as any);
              }
              if (data.systolic && data.diastolic) {
                await api.objects.createObject(token ?? null, {
                  type: 'stat', label: 'Blood Pressure', value: `${data.systolic}/${data.diastolic}`, unit: 'mmHg',
                  date: `${today}T00:00:00.000Z`, source: 'health:vitals', category: 'health',
                  systolic: data.systolic, diastolic: data.diastolic,
                  metricKey: `health:vitals:bp:${today}`,
                  moduleLinks: [{ moduleId: module.id }],
                } as any);
              }
              queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY });
              showToast(data.weight ? 'Weight logged' : 'Blood pressure logged', 'success');
            } catch { showToast('Failed to save', 'error'); }
          }} />
        ))}

        {activeTab === 'profile' && <HealthProfile />}

        {activeTab === 'labs' && <LabResultsView healthStats={allItems} onAddLabReport={() => setShowAddModal(true)} onDeleteReport={async (id) => {
            try {
              await api.objects.deleteObject(token ?? null, 'stat', id);
              queryClient.invalidateQueries({ queryKey: HEALTH_STATS_QUERY_KEY });
              showToast('Lab report deleted', 'success');
            } catch { showToast('Failed to delete', 'error'); }
          }} />}

        {activeTab === 'trends' && (
          <>
            {!hasData && !wearableSkipped ? (
              <>
                <ModuleEmptyState
                  icon={<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" /></svg>}
                  iconColor="#ec4899"
                  title="AI-powered health tracking"
                  description={<>Connect a wearable to sync your health data automatically.<br />Track sleep, recovery, activity, and more with AI insights.</>}
                />
                {renderWearableButtons(skipWearable)}
              </>
            ) : (
              <>
                {/* Gear icon — always visible so user can customize even with 0 stars */}
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Key Scores</span>
                  <div style={{ position: 'relative' }}>
                    <button ref={gearRef} onClick={() => setShowMetricsSettings(!showMetricsSettings)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: 'var(--color-text-faint)' }}
                      title="Customize metrics">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                      </svg>
                    </button>
                    {showMetricsSettings && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, marginTop: 4 }}>
                        <MetricsSettingsPanel
                          metrics={Object.keys(chartData)}
                          starred={starredMetrics}
                          hidden={hiddenMetrics}
                          metricLabels={SOURCE_LABELS}
                          onUpdate={handleMetricsUpdate}
                          onClose={() => setShowMetricsSettings(false)}
                          anchorRef={gearRef}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {summaryCards.filter(c => prefsMatch(starredMetrics, c.source)).length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(summaryCards.filter(c => prefsMatch(starredMetrics, c.source)).length, 4)}, 1fr)`, gap: 10, marginBottom: 16 }}>
                    {summaryCards.filter(c => prefsMatch(starredMetrics, c.source)).map((card) => (
                      <div key={card.source} style={{
                        padding: '14px 14px 10px', borderRadius: 12, background: 'var(--color-surface)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)', position: 'relative', overflow: 'hidden',
                      }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                          {card.label}
                          {card.provider && <span style={{ fontSize: 8, fontWeight: 600, color: '#fff', background: PROVIDER_COLORS[card.provider] || '#9ca3af', borderRadius: 3, padding: '1px 4px', textTransform: 'uppercase' }}>{card.provider}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                          <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                            {typeof card.latest === 'number' ? (card.latest % 1 === 0 ? card.latest : card.latest.toFixed(1)) : card.latest}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)', fontWeight: 500 }}>{card.unit}</span>
                        </div>
                        {card.trend !== 0 && (
                          <div style={{ fontSize: 11, marginTop: 4, color: card.trend > 0 ? '#22c55e' : '#ef4444', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <span>{card.trend > 0 ? '\u2191' : '\u2193'}</span>
                            <span>{Math.abs(card.trend % 1 === 0 ? card.trend : Number(card.trend.toFixed(1)))}</span>
                            <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>vs avg</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {starredMetrics.length === 0 && (
                  <div style={{ padding: '12px 0 16px', fontSize: 13, color: 'var(--color-text-faint)', textAlign: 'center' }}>
                    No starred metrics. Click the gear icon to choose up to 6.
                  </div>
                )}

                {Object.keys(chartData).length > 0 && (() => {
                  const insights = buildInsights(chartData, allItems);
                  if (insights.length === 0) return null;
                  return (
                    <div style={{
                      padding: '12px 16px', marginBottom: 12,
                      borderLeft: '3px solid #0ea5e9',
                    }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                        30-Day Trend Analysis
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {insights.map((text, i) => (
                          <div key={i} style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>{text}</div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {Object.keys(chartData).length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {(() => {
                      const CHART_ORDER = ['weight', 'calories', 'protein', 'exercise', 'systolic', 'diastolic', 'whoop/recovery', 'whoop/strain', 'whoop/sleep', 'whoop/workout'];
                      return Object.entries(chartData)
                        .filter(([key, pts]) => pts.length >= 3 && !prefsMatch(hiddenMetrics, key))
                        .sort(([a], [b]) => {
                          // Starred metrics first
                          const aStarred = prefsMatch(starredMetrics, a) ? 0 : 1;
                          const bStarred = prefsMatch(starredMetrics, b) ? 0 : 1;
                          if (aStarred !== bStarred) return aStarred - bStarred;
                          const ai = CHART_ORDER.indexOf(a);
                          const bi = CHART_ORDER.indexOf(b);
                          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                        });
                    })().map(([source, points]) => {
                      const color = getSourceColor(source);
                      const m = metricOf(source);
                      const prov = providerOf(source);
                      const label = SOURCE_LABELS[m] || SOURCE_LABELS[source] || source;
                      const unitMap: Record<string, string> = { calories: 'cal', protein: 'g', exercise: 'cal', weight: 'lbs', systolic: 'mmHg', diastolic: 'mmHg' };
                      return (
                        <div key={source} style={{ padding: '14px 14px 8px', borderRadius: 12, background: 'var(--color-surface)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color.fg, display: 'inline-block' }} />
                            {label}
                            {prov && <span style={{ fontSize: 9, fontWeight: 500, color: '#fff', background: PROVIDER_COLORS[prov] || '#9ca3af', borderRadius: 4, padding: '1px 5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{prov}</span>}
                          </div>
                          <Sparkline data={points} color={color.fg} height={90} unit={unitMap[m] || unitMap[source]} />
                        </div>
                      );
                    })}
                  </div>
                )}

                {summaryCards.filter(c => !prefsMatch(starredMetrics, c.source) && !prefsMatch(hiddenMetrics, c.source)).length > 0 && (
                  <>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>All Metrics</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 16 }}>
                    {summaryCards.filter(c => !prefsMatch(starredMetrics, c.source) && !prefsMatch(hiddenMetrics, c.source)).map((card) => (
                      <div key={card.source} style={{
                        padding: '10px 10px 8px', borderRadius: 10, background: 'var(--color-surface)',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.03)', overflow: 'hidden',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--color-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {card.label}
                          {card.provider && <span style={{ fontSize: 7, fontWeight: 600, color: '#fff', background: PROVIDER_COLORS[card.provider] || '#9ca3af', borderRadius: 3, padding: '1px 3px', textTransform: 'uppercase', flexShrink: 0 }}>{card.provider}</span>}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 3 }}>
                          <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                            {typeof card.latest === 'number' ? (card.latest % 1 === 0 ? card.latest : card.latest.toFixed(1)) : card.latest}
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontWeight: 500 }}>{card.unit}</span>
                        </div>
                        {card.trend !== 0 && (
                          <div style={{ fontSize: 10, marginTop: 2, color: card.trend > 0 ? '#22c55e' : '#ef4444', fontWeight: 500 }}>
                            {card.trend > 0 ? '\u2191' : '\u2193'}{Math.abs(card.trend % 1 === 0 ? card.trend : Number(card.trend.toFixed(1)))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  </>
                )}

              </>
            )}
          </>
        )}
      </div>

      {showSwitchModal && (
        <SwitchWearableModal
          activeWearable={activeWearable}
          connecting={connecting}
          onConnectProvider={handleConnectProvider}
          onDisconnectWearable={handleDisconnectWearable}
          onClose={() => setShowSwitchModal(false)}
        />
      )}

      <style>{`
        @keyframes health-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {showAddModal && (
        <AddMealModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddEntry}
          submitting={addSubmitting}
          mode={activeTab === 'labs' ? 'labs' : activeTab === 'profile' ? 'profile' : 'log'}
        />
      )}

      {showObjectives && (
        <ObjectivesModal
          objectiveCalories={objectiveCalories}
          objectiveProtein={objectiveProtein}
          onCaloriesChange={setObjectiveCalories}
          onProteinChange={setObjectiveProtein}
          onAIObjectives={handleAIObjectives}
          onSaveObjectives={handleSaveObjectives}
          onClose={() => setShowObjectives(false)}
        />
      )}
    </div>
  );
}
